/* Patient Appointments — iter 98.13 redesign.
 *
 * Translated from the user-supplied "Appointments" HTML mockup, with the
 * Apollo cyan-teal `primary` token preserved instead of the mockup's
 * emerald palette.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  Page header (title + Book CTA)                         │
 *   │  Hero "Next appointment" card (gradient + live          │
 *   │     countdown) — only when there's an upcoming visit    │
 *   │  Upcoming list — date block + title + badges + actions  │
 *   │  Past appointments — collapsible "Show all" toggle      │
 *   │  Your clinic — directions / call / message              │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Data flows preserved (no backend changes):
 *   /appointments/my                              — patient agenda
 *   /patient/appointments/reschedule-requests     — pending/decided RR's
 *   /patient/me/care-team                         — treating doctor (for
 *                                                   the Book button to
 *                                                   route to /book/{id}
 *                                                   instead of /find-doctor)
 *
 * Reschedule + Cancel still go through the existing dialogs
 * (RescheduleRequestDialog + CancelAppointmentDialog) so the proper
 * "propose 3 slots" loop + reason-picker UX are kept intact.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    CalendarBlank, Clock, ArrowsClockwise, X, Plus, Warning, MapPin,
    Phone, ChatCircle, Buildings, NavigationArrow, FileText,
} from "@phosphor-icons/react";
import RescheduleRequestDialog from "@/components/patient/RescheduleRequestDialog";
import CancelAppointmentDialog from "@/components/patient/CancelAppointmentDialog";
import DoctorAvatar from "@/components/common/DoctorAvatar";

// ────────────────────────────────────────────────────────────────────
// Helpers — formatting + classification
// ────────────────────────────────────────────────────────────────────
const STATUS_BADGE = {
    confirmed:    { label: "Upcoming",    cls: "bg-primary/10 text-primary ring-1 ring-primary/20" },
    pending:      { label: "Pending",     cls: "bg-amber-50 text-amber-800 ring-1 ring-amber-200" },
    completed:    { label: "Completed",   cls: "bg-secondary text-muted-foreground ring-1 ring-border" },
    cancelled:    { label: "Cancelled",   cls: "bg-rose-50 text-rose-700 ring-1 ring-rose-200" },
    rescheduled:  { label: "Rescheduled", cls: "bg-blue-50 text-blue-700 ring-1 ring-blue-200" },
    no_show:      { label: "No-show",     cls: "bg-rose-50 text-rose-700 ring-1 ring-rose-200" },
};

const isUpcoming = (a) => {
    if (["cancelled", "completed", "rescheduled", "no_show"].includes(a.status)) return false;
    try {
        const apptDate = new Date(`${a.date}T${a.time || "00:00"}:00`);
        return apptDate.getTime() >= Date.now() - 4 * 60 * 60 * 1000; // 4h grace
    } catch { return true; }
};

const toApptDate = (a) => {
    try { return new Date(`${a.date}T${a.time || "00:00"}:00`); }
    catch { return null; }
};

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
            weekday: "long", day: "2-digit", month: "long", year: "numeric",
        });
    } catch { return iso; }
};

const fmtTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    if (Number.isNaN(h)) return t;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m || 0).padStart(2, "0")} ${period}`;
};

const fmtTimeRange = (t, mins = 30) => {
    const start = fmtTime(t);
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const endMins = (h * 60 + (m || 0)) + Number(mins || 30);
    const eh = Math.floor(endMins / 60) % 24;
    const em = endMins % 60;
    const end = fmtTime(`${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`);
    return `${start} – ${end}`;
};

const dayNum = (iso) => {
    if (!iso) return "";
    try { return new Date(`${iso}T00:00:00`).getDate(); } catch { return ""; }
};
const monthShort = (iso) => {
    if (!iso) return "";
    try {
        return new Date(`${iso}T00:00:00`)
            .toLocaleDateString("en-IN", { month: "short" })
            .toUpperCase();
    } catch { return ""; }
};

const typeLabel = (a) => {
    const t = (a.appointment_type || a.type || "consultation").replace(/_/g, " ");
    return t.charAt(0).toUpperCase() + t.slice(1);
};

// Patients see a "Retention" badge tagged alongside the status for any
// retention-related appointment — gives the doctor + patient a quick
// visual classifier matching the mockup.
const isRetention = (a) => {
    const t = (a.appointment_type || a.type || "").toLowerCase();
    return /retention|retainer|recall/.test(t);
};


// ────────────────────────────────────────────────────────────────────
// Live countdown for the hero card — ticks every second.
// ────────────────────────────────────────────────────────────────────
function useCountdown(target) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!target) return undefined;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [target]);
    if (!target) return null;
    const diff = target.getTime() - now;
    if (diff <= 0) return { ready: true, days: 0, hrs: 0, mins: 0, secs: 0 };
    return {
        ready: false,
        days: Math.floor(diff / 86400000),
        hrs:  Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
    };
}


// ────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────
export default function PatientAppointments() {
    const navigate = useNavigate();
    const [appts, setAppts] = useState([]);
    const [rescheduleRequests, setRescheduleRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rescheduleTarget, setRescheduleTarget] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [showPastAll, setShowPastAll] = useState(false);
    const [treatingDoctorId, setTreatingDoctorId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [a, rr, ct] = await Promise.all([
                api.get("/appointments/my"),
                api.get("/patient/appointments/reschedule-requests").catch(() => ({ data: [] })),
                api.get("/patient/me/care-team").catch(() => ({ data: null })),
            ]);
            setAppts(a.data || []);
            setRescheduleRequests(rr.data || []);
            setTreatingDoctorId(ct.data?.doctor?.id || null);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Existing patients route to their doctor's booking page. Patients
    // without a treating doctor (shouldn't normally happen — every patient
    // in the portal has at least one appointment-linked clinic) fall back
    // to the appointments page itself so we never bounce them out to the
    // public marketing /find-doctor flow.
    const goBookNew = () => {
        if (treatingDoctorId) navigate(`/book/${treatingDoctorId}`);
        else navigate("/patient/appointments");
    };

    const pendingByAppt = useMemo(() => {
        const m = {};
        for (const r of rescheduleRequests) {
            if (r.status === "pending") m[r.appointment_id] = r;
        }
        return m;
    }, [rescheduleRequests]);

    const lastDecidedByAppt = useMemo(() => {
        const m = {};
        for (const r of rescheduleRequests) {
            if (r.status !== "pending" && !m[r.appointment_id]) {
                m[r.appointment_id] = r;
            }
        }
        return m;
    }, [rescheduleRequests]);

    const upcoming = useMemo(
        () => appts
            .filter(isUpcoming)
            .sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`)),
        [appts],
    );

    const past = useMemo(
        () => appts
            .filter((a) => !isUpcoming(a) && a.status !== "cancelled")
            .sort((a, b) => `${b.date} ${b.time || ""}`.localeCompare(`${a.date} ${a.time || ""}`)),
        [appts],
    );

    const cancelled = useMemo(
        () => appts.filter((a) => a.status === "cancelled"),
        [appts],
    );

    const nextAppt = upcoming[0] || null;
    const nextApptDate = nextAppt ? toApptDate(nextAppt) : null;

    return (
        <div className="max-w-4xl" data-testid="patient-appointments-page">
            {/* ─────────── Page header ─────────── */}
            <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-bold">Appointments</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {nextAppt?.doctor_name
                            ? `Manage and book your visits with ${nextAppt.doctor_name}.`
                            : "Manage and book your visits."}
                    </p>
                </div>
                <button
                    onClick={goBookNew}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition"
                    data-testid="book-new-appt-btn"
                >
                    <Plus size={14} weight="bold" /> Book appointment
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground p-6" data-testid="appts-loading">Loading your appointments…</div>
            ) : (
                <div className="space-y-3">
                    {/* ─────────── Hero "Next appointment" card ─────────── */}
                    {nextAppt && (
                        <NextAppointmentHero
                            appt={nextAppt}
                            apptDate={nextApptDate}
                            onReschedule={() => setRescheduleTarget(nextAppt)}
                            onCancel={() => setCancelTarget(nextAppt)}
                            pendingRequest={pendingByAppt[nextAppt.id]}
                        />
                    )}

                    {/* ─────────── Upcoming list ─────────── */}
                    <SectionCard
                        title="Upcoming"
                        action={
                            <button
                                onClick={goBookNew}
                                className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                                data-testid="upcoming-book-new"
                            >
                                <Plus size={12} weight="bold" /> Book new
                            </button>
                        }
                        testid="appts-upcoming-section"
                    >
                        {upcoming.length === 0 ? (
                            <EmptyState
                                title="No upcoming appointments"
                                body={treatingDoctorId ? "Book your next visit and we'll send you a confirmation." : "Once your clinic adds an appointment for you, it will show up here."}
                                ctaLabel={treatingDoctorId ? "Book with your doctor" : null}
                                onCta={treatingDoctorId ? goBookNew : undefined}
                            />
                        ) : (
                            <div>
                                {upcoming.map((a, idx) => (
                                    <AppointmentItem
                                        key={a.id}
                                        appt={a}
                                        accent
                                        isLast={idx === upcoming.length - 1}
                                        pendingRequest={pendingByAppt[a.id]}
                                        lastDecided={lastDecidedByAppt[a.id]}
                                        onReschedule={() => setRescheduleTarget(a)}
                                        onCancel={() => setCancelTarget(a)}
                                    />
                                ))}
                            </div>
                        )}
                    </SectionCard>

                    {/* ─────────── Past appointments ─────────── */}
                    {past.length > 0 && (
                        <SectionCard
                            title="Past appointments"
                            action={
                                past.length > 2 ? (
                                    <button
                                        onClick={() => setShowPastAll((v) => !v)}
                                        className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                                        data-testid="toggle-past-btn"
                                    >
                                        {showPastAll ? "Show less" : `Show all (${past.length})`}
                                    </button>
                                ) : null
                            }
                            testid="appts-past-section"
                        >
                            {(showPastAll ? past : past.slice(0, 2)).map((a, idx, arr) => (
                                <AppointmentItem
                                    key={a.id}
                                    appt={a}
                                    isLast={idx === arr.length - 1}
                                    past
                                />
                            ))}
                        </SectionCard>
                    )}

                    {/* ─────────── Cancelled (compact) ─────────── */}
                    {cancelled.length > 0 && (
                        <SectionCard
                            title={`Cancelled (${cancelled.length})`}
                            testid="appts-cancelled-section"
                            muted
                        >
                            {cancelled.slice(0, 5).map((a, idx, arr) => (
                                <AppointmentItem
                                    key={a.id}
                                    appt={a}
                                    isLast={idx === arr.length - 1}
                                    dim
                                />
                            ))}
                        </SectionCard>
                    )}

                    {/* ─────────── Your clinic ─────────── */}
                    {nextAppt && (
                        <ClinicCard
                            clinicName={nextAppt.clinic_name}
                            doctorName={nextAppt.doctor_name}
                            doctorId={treatingDoctorId}
                        />
                    )}
                </div>
            )}

            {/* ─────────── Dialogs ─────────── */}
            {rescheduleTarget && (
                <RescheduleRequestDialog
                    open={true}
                    appt={rescheduleTarget}
                    onClose={() => setRescheduleTarget(null)}
                    onSuccess={() => { setRescheduleTarget(null); load(); }}
                />
            )}
            <CancelAppointmentDialog
                open={!!cancelTarget}
                appt={cancelTarget}
                onOpenChange={(o) => !o && setCancelTarget(null)}
                onCancelled={load}
            />
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────
// NextAppointmentHero — gradient card with live countdown.
// Only renders when there's at least one upcoming appointment.
// ────────────────────────────────────────────────────────────────────
function NextAppointmentHero({ appt, apptDate, onReschedule, onCancel, pendingRequest }) {
    const cd = useCountdown(apptDate);
    const dur = appt.duration_minutes || 30;

    return (
        <div
            className="relative overflow-hidden rounded-2xl p-6 text-white shadow-lg shadow-primary/20"
            style={{
                background: "linear-gradient(135deg, hsl(193 100% 22%), hsl(192 100% 31%), hsl(173 58% 42%))",
            }}
            data-testid="next-appt-hero"
        >
            {/* Decorative circle */}
            <div
                className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full"
                style={{ background: "rgba(255,255,255,0.06)" }}
            />

            <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/70 flex items-center gap-1.5 mb-2">
                    <span className="w-2 h-2 rounded-full bg-white/80 animate-pulse" />
                    Next appointment
                </div>

                <h2 className="font-display text-xl sm:text-2xl font-bold mb-2">
                    {typeLabel(appt)}
                </h2>

                <div className="flex flex-wrap gap-x-5 gap-y-2 mb-5 text-sm text-white/85">
                    <Meta icon={CalendarBlank}>{fmtDate(appt.date)}</Meta>
                    <Meta icon={Clock}>{fmtTimeRange(appt.time, dur)}</Meta>
                    {appt.clinic_name && <Meta icon={MapPin}>{appt.clinic_name}</Meta>}
                    {appt.doctor_name && (
                        <span className="inline-flex items-center gap-1.5">
                            <DoctorAvatar
                                src={appt.doctor_image}
                                name={appt.doctor_name}
                                size={18}
                                initialsClassName="bg-white/20 border border-white/30 text-white text-[8px]"
                            />
                            {appt.doctor_name}
                        </span>
                    )}
                </div>

                {/* Countdown */}
                {cd && !cd.ready && (
                    <div className="flex flex-wrap gap-2 mb-5" data-testid="appt-countdown">
                        <CountdownChip value={cd.days} unit="DAYS" />
                        <CountdownChip value={String(cd.hrs).padStart(2, "0")} unit="HRS" />
                        <CountdownChip value={String(cd.mins).padStart(2, "0")} unit="MIN" />
                        <CountdownChip value={String(cd.secs).padStart(2, "0")} unit="SEC" />
                    </div>
                )}
                {cd?.ready && (
                    <div className="text-sm text-white/90 mb-5 font-medium">
                        Your appointment is starting now.
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                    <HeroBtn
                        onClick={() => {
                            if (!appt.clinic_name) return;
                            window.open(
                                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appt.clinic_name)}`,
                                "_blank",
                            );
                        }}
                        data-testid="hero-directions-btn"
                    >
                        <NavigationArrow size={14} weight="fill" />
                        Get directions
                    </HeroBtn>
                    <HeroBtn
                        onClick={onReschedule}
                        disabled={!!pendingRequest}
                        title={pendingRequest ? "A reschedule request is already pending" : ""}
                        data-testid="hero-reschedule-btn"
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        Reschedule
                    </HeroBtn>
                    <HeroBtn
                        ghost
                        onClick={onCancel}
                        data-testid="hero-cancel-btn"
                    >
                        <X size={14} weight="bold" />
                        Cancel
                    </HeroBtn>
                </div>

                {/* Pending banner — collapsed into a tone-matching strip */}
                {pendingRequest && (
                    <div
                        className="mt-4 rounded-lg px-3 py-2 text-xs flex items-start gap-2"
                        style={{ background: "rgba(255,255,255,0.12)" }}
                        data-testid="hero-pending-reschedule"
                    >
                        <Warning size={14} weight="fill" className="shrink-0 mt-0.5 text-white" />
                        <div className="text-white/90">
                            <span className="font-semibold">Reschedule request sent</span> — we&apos;ll notify you as soon as the clinic decides on the {pendingRequest.preferred_slots?.length || 0} slot{pendingRequest.preferred_slots?.length !== 1 ? "s" : ""} you proposed.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const Meta = ({ icon: Icon, children }) => (
    <span className="inline-flex items-center gap-1.5">
        <Icon size={15} className="opacity-75" /> {children}
    </span>
);

const CountdownChip = ({ value, unit }) => (
    <div
        className="flex flex-col items-center min-w-[64px] rounded-lg px-4 py-2.5"
        style={{ background: "rgba(255,255,255,0.15)" }}
    >
        <div className="text-2xl font-bold tabular-nums leading-none">{value}</div>
        <div className="text-[10px] tracking-wider text-white/70 mt-1">{unit}</div>
    </div>
);

const HeroBtn = ({ children, ghost, disabled, title, ...rest }) => (
    <button
        disabled={disabled}
        title={title}
        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
            ghost
                ? "bg-transparent border border-white/20 text-white/75 hover:bg-white/10"
                : "bg-white/15 border border-white/30 text-white hover:bg-white/25 backdrop-blur-sm"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        {...rest}
    >
        {children}
    </button>
);


// ────────────────────────────────────────────────────────────────────
// SectionCard — section title bar + body container.
// ────────────────────────────────────────────────────────────────────
function SectionCard({ title, action, children, testid, muted }) {
    return (
        <div
            className={`rounded-2xl border border-border bg-card px-5 py-4 ${muted ? "opacity-90" : ""}`}
            data-testid={testid}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="w-[3px] h-4 rounded-full bg-primary" />
                    <span className="text-sm font-semibold text-foreground">{title}</span>
                </div>
                {action}
            </div>
            <div>{children}</div>
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────
// AppointmentItem — single row with date block + info + actions.
// ────────────────────────────────────────────────────────────────────
function AppointmentItem({
    appt, accent = false, past = false, dim = false,
    isLast = false, pendingRequest, lastDecided,
    onReschedule, onCancel,
}) {
    const status = STATUS_BADGE[appt.status] || STATUS_BADGE.confirmed;
    const ret = isRetention(appt);

    return (
        <div
            className={`flex items-start gap-3 py-3.5 ${isLast ? "" : "border-b border-border"} ${dim ? "opacity-60" : ""}`}
            data-testid={`appt-item-${appt.id}`}
        >
            {/* Date block */}
            <div
                className={`shrink-0 w-11 text-center rounded-lg py-1.5 px-1 border ${
                    accent
                        ? "bg-primary/10 border-primary/30"
                        : "bg-secondary border-border"
                }`}
            >
                <div className={`text-lg font-bold leading-none ${accent ? "text-primary" : "text-foreground"}`}>
                    {dayNum(appt.date)}
                </div>
                <div className={`text-[9px] font-semibold tracking-wider mt-0.5 ${accent ? "text-primary/80" : "text-muted-foreground"}`}>
                    {monthShort(appt.date)}
                </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className="text-sm font-medium truncate">{typeLabel(appt)}</div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${status.cls}`}>
                        {status.label}
                    </span>
                    {ret && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                            Retention
                        </span>
                    )}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                        <Clock size={12} className="text-muted-foreground/70" />
                        {fmtTimeRange(appt.time, appt.duration_minutes || 30)}
                    </span>
                    {appt.clinic_name && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin size={12} className="text-muted-foreground/70" />
                            {appt.clinic_name}
                        </span>
                    )}
                    {appt.doctor_name && (
                        <span className="inline-flex items-center gap-1 truncate">
                            <Buildings size={12} className="text-muted-foreground/70" />
                            {appt.doctor_name}
                        </span>
                    )}
                </div>

                {/* Pending banner — row-level */}
                {pendingRequest && (
                    <div
                        className="mt-2 rounded-md px-2.5 py-1.5 bg-amber-50/60 border border-amber-200/60 text-[11px] text-amber-900 flex items-start gap-1.5"
                        data-testid={`pending-reschedule-${appt.id}`}
                    >
                        <Warning size={12} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                        <span>
                            <span className="font-semibold">Reschedule request sent</span> — waiting on the clinic to confirm one of your {pendingRequest.preferred_slots?.length || 0} proposed slot{pendingRequest.preferred_slots?.length !== 1 ? "s" : ""}.
                        </span>
                    </div>
                )}
                {!pendingRequest && lastDecided?.status === "declined" && lastDecided.decision_note && (
                    <div
                        className="mt-2 rounded-md px-2.5 py-1.5 bg-rose-50/60 border border-rose-200/60 text-[11px] text-rose-900 flex items-start gap-1.5"
                        data-testid={`declined-reschedule-${appt.id}`}
                    >
                        <Warning size={12} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                        <span>
                            <span className="font-semibold">Previous request declined.</span> <span className="italic">&ldquo;{lastDecided.decision_note}&rdquo;</span>
                        </span>
                    </div>
                )}
            </div>

            {/* Actions */}
            {!past && !dim && (onReschedule || onCancel) && (
                <div className="flex gap-1.5 shrink-0">
                    {onReschedule && (
                        <RowBtn
                            onClick={onReschedule}
                            disabled={!!pendingRequest}
                            title={pendingRequest ? "A request is already pending" : ""}
                            data-testid={`reschedule-btn-${appt.id}`}
                        >
                            <ArrowsClockwise size={12} weight="bold" />
                            <span className="hidden sm:inline">Reschedule</span>
                        </RowBtn>
                    )}
                    {onCancel && (
                        <RowBtn
                            danger
                            onClick={onCancel}
                            data-testid={`cancel-btn-${appt.id}`}
                            aria-label="Cancel appointment"
                        >
                            <X size={12} weight="bold" />
                        </RowBtn>
                    )}
                </div>
            )}
            {past && (
                <div className="shrink-0">
                    <RowBtn disabled title="Visit summary — coming soon" data-testid={`summary-btn-${appt.id}`}>
                        <FileText size={12} />
                        <span className="hidden sm:inline">Summary</span>
                    </RowBtn>
                </div>
            )}
        </div>
    );
}

const RowBtn = ({ children, danger, ...rest }) => (
    <button
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition whitespace-nowrap ${
            danger
                ? "border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300"
                : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground hover:border-primary/30"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        {...rest}
    >
        {children}
    </button>
);


// ────────────────────────────────────────────────────────────────────
// ClinicCard — quick links to the clinic the patient is visiting.
// ────────────────────────────────────────────────────────────────────
function ClinicCard({ clinicName, doctorName, doctorId }) {
    const navigate = useNavigate();
    if (!clinicName && !doctorName) return null;
    return (
        <div
            className="rounded-2xl border border-border bg-card px-5 py-4"
            data-testid="clinic-card"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="w-[3px] h-4 rounded-full bg-primary" />
                    <span className="text-sm font-semibold text-foreground">Your clinic</span>
                </div>
            </div>
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <Buildings size={20} weight="duotone" className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{clinicName || "Your clinic"}</div>
                    {doctorName && (
                        <div className="text-xs text-muted-foreground mt-0.5">{doctorName}</div>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {clinicName && (
                            <RowBtn
                                onClick={() => window.open(
                                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clinicName)}`,
                                    "_blank",
                                )}
                                data-testid="clinic-directions-btn"
                            >
                                <NavigationArrow size={12} weight="fill" /> Directions
                            </RowBtn>
                        )}
                        {doctorId && (
                            <RowBtn
                                onClick={() => navigate("/patient/chat")}
                                data-testid="clinic-message-doctor-btn"
                            >
                                <ChatCircle size={12} weight="fill" /> Message doctor
                            </RowBtn>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────
// EmptyState — used inside the Upcoming section when zero rows.
// ────────────────────────────────────────────────────────────────────
function EmptyState({ title, body, ctaLabel, onCta }) {
    return (
        <div className="py-8 text-center" data-testid="appts-empty">
            <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center mx-auto mb-3">
                <CalendarBlank size={18} weight="duotone" className="text-primary" />
            </div>
            <div className="text-sm font-semibold mb-1">{title}</div>
            <div className="text-xs text-muted-foreground max-w-sm mx-auto mb-4">{body}</div>
            {ctaLabel && (
                <button
                    onClick={onCta}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition"
                    data-testid="empty-book-btn"
                >
                    <Plus size={12} weight="bold" /> {ctaLabel}
                </button>
            )}
        </div>
    );
}
